import { AnalyticsConsentService, AnalyticsConsentState } from './AnalyticsConsentService';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const GA_ENABLED = String(process.env.REACT_APP_GA_ENABLED || 'false').toLowerCase() === 'true';
const SCRIPT_ID = 'ts-google-analytics-gtag';
const DEBUG_QUERY_PARAM = 'ts_ga_debug';

type GtagCommand = [command: string, ...args: unknown[]];

declare global {
  interface Window {
    dataLayer?: Array<GtagCommand | Record<string, unknown>>;
    gtag?: (...args: GtagCommand) => void;
  }
}

const hasBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

const getCurrentPath = (): string => {
  if (!hasBrowser()) return '/';
  return window.location.pathname || '/';
};

const isDebugMode = (): boolean => {
  if (!hasBrowser()) return false;
  return new URLSearchParams(window.location.search).get(DEBUG_QUERY_PARAM) === '1';
};

export interface GoogleAnalyticsDebugInfo {
  configured: boolean;
  enabled: boolean;
  measurementIdPreview: string;
  consentState: AnalyticsConsentState;
  scriptLoaded: boolean;
  debugMode: boolean;
  disabledFlag: boolean;
  dataLayerCommands: string[];
  queuedEvents: string[];
}

export class GoogleAnalyticsService {
  private static bootstrapped = false;
  private static configured = false;
  private static scriptLoading = false;
  private static unsubscribeConsent: (() => void) | null = null;

  static isConfigured(): boolean {
    return GA_ENABLED && Boolean(GA_MEASUREMENT_ID);
  }

  static isEnabled(): boolean {
    return this.isConfigured() && hasBrowser() && AnalyticsConsentService.hasGranted();
  }

  static getDebugInfo(): GoogleAnalyticsDebugInfo {
    const dataLayer = hasBrowser() ? window.dataLayer || [] : [];

    return {
      configured: this.isConfigured(),
      enabled: this.isEnabled(),
      measurementIdPreview: GA_MEASUREMENT_ID ? `${GA_MEASUREMENT_ID.slice(0, 5)}...${GA_MEASUREMENT_ID.slice(-4)}` : 'missing',
      consentState: AnalyticsConsentService.getState(),
      scriptLoaded: hasBrowser() && Boolean(document.getElementById(SCRIPT_ID)),
      debugMode: isDebugMode(),
      disabledFlag: hasBrowser() ? Boolean((window as any)[`ga-disable-${GA_MEASUREMENT_ID}`]) : false,
      dataLayerCommands: dataLayer
        .map((entry) => Array.isArray(entry) ? String(entry[0]) : typeof entry)
        .slice(-12),
      queuedEvents: dataLayer
        .filter((entry) => Array.isArray(entry) && entry[0] === 'event')
        .map((entry) => String(entry[1] || 'unknown'))
        .slice(-8)
    };
  }

  static bootstrap(): void {
    if (!this.isConfigured() || !hasBrowser()) return;

    this.ensureDataLayer();
    this.setDefaultConsent();

    if (AnalyticsConsentService.hasGranted()) {
      this.enableAnalytics();
    } else {
      this.disableAnalytics();
    }

    if (!this.unsubscribeConsent) {
      this.unsubscribeConsent = AnalyticsConsentService.subscribe((state) => {
        if (state === 'granted') {
          this.enableAnalytics();
        } else if (state === 'denied') {
          this.disableAnalytics();
        }
      });
    }

    this.bootstrapped = true;
  }

  static enableAnalytics(options: { sendInitialPageView?: boolean } = {}): void {
    if (!this.isConfigured() || !hasBrowser()) return;

    this.ensureDataLayer();
    (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

    window.gtag?.('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    });

    this.loadScript();
    this.configure();

    if (options.sendInitialPageView) {
      this.trackPageView(getCurrentPath(), document.title || undefined);
    }
  }

  static disableAnalytics(): void {
    if (!this.isConfigured() || !hasBrowser()) return;

    this.ensureDataLayer();
    (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

    window.gtag?.('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    });
  }

  static trackPageView(path: string = getCurrentPath(), title?: string): void {
    if (!this.isEnabled()) return;

    this.ensureDataLayer();
    this.loadScript();
    this.configure();

    window.gtag?.('event', 'page_view', {
      page_path: path || '/',
      page_location: `${window.location.origin}${path || '/'}`,
      page_title: title || document.title || undefined,
      debug_mode: isDebugMode() || undefined
    });
  }

  static trackEvent(eventName: string, params: Record<string, unknown> = {}): void {
    if (!this.isEnabled() || !eventName) return;

    this.ensureDataLayer();
    this.loadScript();
    this.configure();

    window.gtag?.('event', eventName, {
      ...params,
      debug_mode: isDebugMode() || undefined
    });
  }

  static sendManualDebugEvent(): { success: boolean; message: string } {
    if (!this.isConfigured()) {
      return { success: false, message: 'Google Analytics non configurato' };
    }

    if (!AnalyticsConsentService.hasGranted()) {
      return { success: false, message: 'Consenso analytics non concesso' };
    }

    this.trackEvent('ops_manual_ga_debug', {
      event_category: 'ops',
      current_path: getCurrentPath(),
      current_host: hasBrowser() ? window.location.host : undefined,
      sent_at: new Date().toISOString()
    });

    return { success: true, message: 'Evento GA inviato: ops_manual_ga_debug' };
  }

  private static ensureDataLayer(): void {
    if (!hasBrowser()) return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || ((...args: GtagCommand) => {
      window.dataLayer?.push(args);
    });
  }

  private static setDefaultConsent(): void {
    if (!hasBrowser()) return;

    window.gtag?.('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500
    });
  }

  private static loadScript(): void {
    if (!hasBrowser() || this.scriptLoading || document.getElementById(SCRIPT_ID)) return;

    this.scriptLoading = true;
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
    script.onload = () => {
      this.scriptLoading = false;
    };
    script.onerror = () => {
      this.scriptLoading = false;
      console.warn('Google Analytics script failed to load');
    };
    document.head.appendChild(script);
  }

  private static configure(): void {
    if (this.configured || !hasBrowser()) return;

    this.ensureDataLayer();
    window.gtag?.('js', new Date());
    window.gtag?.('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      anonymize_ip: true,
      allow_ad_personalization_signals: false,
      debug_mode: isDebugMode() || undefined
    });
    this.configured = true;
  }
}

export default GoogleAnalyticsService;
